import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { adminGet } from "../../lib/adminApi";

export function NoShowModal({ werknemerNaam, onClose, onSubmit, loading }) {
  const [notitie, setNotitie] = useState("");
  return (
    <div role="dialog" aria-modal="true" className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 px-4" data-testid="modal-no-show">
      <div className="w-full max-w-md rounded-lg bg-white p-5 shadow-lg">
        <h3 className="text-base font-semibold text-slate-900">No-show registreren</h3>
        <p className="mt-1 text-xs text-slate-600">Werknemer: {werknemerNaam}</p>
        <label className="mt-4 block text-sm">
          <span className="font-medium text-slate-700">Notitie (optioneel)</span>
          <textarea value={notitie} onChange={(e) => setNotitie(e.target.value)} rows={3} maxLength={500}
            data-testid="modal-no-show-notitie"
            className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm" />
        </label>
        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onClose} data-testid="modal-no-show-cancel"
            className="rounded-md border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700">Annuleren</button>
          <button type="button" disabled={loading} onClick={() => onSubmit({ notitie: notitie || null })}
            data-testid="modal-no-show-submit"
            className="rounded-md bg-rose-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60">
            {loading ? "Bezig…" : "Registreer no-show"}
          </button>
        </div>
      </div>
    </div>
  );
}

export function VerzetModal({ werknemerNaam, currentAanmeetdagId, onClose, onSubmit, loading }) {
  const [aanmeetdagId, setAanmeetdagId] = useState("");
  const [tijdslotId, setTijdslotId] = useState("");
  const [reden, setReden] = useState("");

  const dagenQ = useQuery({
    queryKey: ["aanmeetdagen", "verzet"],
    queryFn: () => adminGet("/api/admin/aanmeetdagen", { status: "bevestigd", limit: 50 }),
  });
  const tijdslotenQ = useQuery({
    queryKey: ["tijdsloten", aanmeetdagId],
    queryFn: () => adminGet(`/api/admin/aanmeetdagen/${aanmeetdagId}/tijdsloten`),
    enabled: Boolean(aanmeetdagId),
  });

  const dagen = (dagenQ.data?.items || []).filter((d) => d.aanmeetdag_id !== currentAanmeetdagId);
  const sloten = tijdslotenQ.data?.items || [];
  const valid = aanmeetdagId && tijdslotId && reden.trim().length >= 5;

  return (
    <div role="dialog" aria-modal="true" className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 px-4" data-testid="modal-verzet">
      <div className="w-full max-w-md rounded-lg bg-white p-5 shadow-lg">
        <h3 className="text-base font-semibold text-slate-900">Aanmeting verzetten</h3>
        <p className="mt-1 text-xs text-slate-600">{werknemerNaam}</p>
        <label className="mt-4 block text-sm">
          <span className="font-medium text-slate-700">Nieuwe aanmeetdag</span>
          <select value={aanmeetdagId} onChange={(e) => { setAanmeetdagId(e.target.value); setTijdslotId(""); }}
            aria-label="Nieuwe aanmeetdag" data-testid="modal-verzet-dag"
            className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm">
            <option value="">— Kies een dag —</option>
            {dagen.map((d) => (
              <option key={d.aanmeetdag_id} value={d.aanmeetdag_id}>
                {d.datum} — {d.aanmeet_locatie}
              </option>
            ))}
          </select>
          {/* Zelfde reden als bij het tijdslot hieronder: een overgeslagen aanmeetdag
              verdwijnt stil uit deze keuzelijst. */}
          {dagenQ.data?.overgeslagen > 0 ? (
            <span data-testid="modal-verzet-dag-overgeslagen" role="alert"
              className="mt-1 block text-xs text-amber-700">
              Niet alle aanmeetdagen konden worden getoond; de reden staat in het
              serverlogboek.
            </span>
          ) : null}
        </label>
        <label className="mt-3 block text-sm">
          <span className="font-medium text-slate-700">Tijdslot</span>
          <select value={tijdslotId} onChange={(e) => setTijdslotId(e.target.value)}
            disabled={!aanmeetdagId} aria-label="Tijdslot" data-testid="modal-verzet-slot"
            className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm disabled:bg-slate-100">
            <option value="">— Kies een tijdslot —</option>
            {sloten.map((s) => (
              <option key={s.tijdslot_id} value={s.tijdslot_id}>
                {s.start_tijd?.slice(0,5) || s.start_tijd} — {s.eind_tijd?.slice(0,5) || s.eind_tijd}
              </option>
            ))}
          </select>
          {/* Een tijdslot dat de server heeft overgeslagen valt hier uit de keuzelijst.
              Zonder deze regel verzet de beheerder de aanmeting naar een ander tijdstip
              zonder te weten dat het gewenste slot bestaat. */}
          {tijdslotenQ.data?.overgeslagen > 0 ? (
            <span data-testid="modal-verzet-slot-overgeslagen" role="alert"
              className="mt-1 block text-xs text-amber-700">
              Niet alle tijdsloten van deze dag konden worden getoond; de reden staat in
              het serverlogboek.
            </span>
          ) : null}
        </label>
        <label className="mt-3 block text-sm">
          <span className="font-medium text-slate-700">Reden (verplicht, ≥5 chars)</span>
          <textarea value={reden} onChange={(e) => setReden(e.target.value)} rows={2}
            data-testid="modal-verzet-reden"
            className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm" />
        </label>
        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onClose} data-testid="modal-verzet-cancel"
            className="rounded-md border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700">Annuleren</button>
          <button type="button" disabled={!valid || loading}
            onClick={() => onSubmit({ nieuwe_aanmeetdag_id: aanmeetdagId, nieuwe_tijdslot_id: tijdslotId, reden })}
            data-testid="modal-verzet-submit"
            className="rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60">
            {loading ? "Bezig…" : "Verzetten"}
          </button>
        </div>
      </div>
    </div>
  );
}
